import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Loader2, UserCog, IdCard } from 'lucide-react';
import { api, friendlyError, unwrap } from '@/lib/api';
import { DASHBOARD } from '@/constants/testIds';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { SectionTitle } from '../SectionTitle';
import { ChangePasswordSection } from './ChangePasswordSection';

// Etiquetas legibles para `profile_type`. Reutiliza los mismos códigos
// definidos en el backend (`models/enums.py::ProfileType`). No introduce
// campos nuevos ni altera la lógica de perfiles.
const PROFILE_TYPE_LABELS = {
  standard_user: 'Aficionado',
  parent_fan: 'Familiar / Aficionado',
  player: 'Jugador / Jugadora',
  coach: 'Entrenador / Entrenadora',
  scout: 'Scout / Captador',
};

export function ProfileTab() {
  const { user, fetchMe } = useAuth();
  const [form, setForm] = useState({
    first_name: '', last_name: '', marketing_consent: false,
  });
  const [profileType, setProfileType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/users/me');
        const data = unwrap(res);
        if (!alive) return;
        setForm({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          marketing_consent: !!data.marketing_consent,
        });
        setProfileType(data.profile_type || null);
      } catch (err) {
        toast.error(friendlyError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api.patch('/users/me', {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        marketing_consent: !!form.marketing_consent,
      });
      toast.success('Datos actualizados.');
      await fetchMe();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 sm:gap-6">
      <Card className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
        <SectionTitle icon={UserCog} label="Mi perfil" hint="Tu información personal y preferencias." />
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-11 w-full rounded-xl" />
          <Skeleton className="h-11 w-full rounded-xl" />
          <Skeleton className="h-11 w-full rounded-xl" />
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dash-first-name" className="text-sm font-medium">Nombre</Label>
              <Input
                id="dash-first-name"
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                data-testid={DASHBOARD.profileFirstName}
                className="h-11 bg-white"
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dash-last-name" className="text-sm font-medium">Apellidos</Label>
              <Input
                id="dash-last-name"
                value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                data-testid={DASHBOARD.profileLastName}
                className="h-11 bg-white"
                required
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium">Email</Label>
            <Input value={user?.email || ''} disabled className="h-11 bg-[var(--ns-bg-2)]" />
            <p className="text-[11px] text-[var(--ns-text-subtle)]">
              Para cambiar tu email, contacta con soporte.
            </p>
          </div>
          <label className="flex items-start gap-2 text-sm text-[var(--ns-text)] cursor-pointer">
            <Checkbox
              checked={form.marketing_consent}
              onCheckedChange={(v) => setForm((f) => ({ ...f, marketing_consent: !!v }))}
              data-testid={DASHBOARD.profileMarketing}
              className="mt-0.5"
            />
            Quiero recibir novedades, eventos y promociones por email.
          </label>
          <Button
            type="submit"
            disabled={saving}
            data-testid={DASHBOARD.profileSubmit}
            className="h-11 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white hover:shadow-[var(--ns-glow-gold)] border-0 transition-all"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar cambios'}
          </Button>
        </form>
      )}
      </Card>
      {/*
        Bloque informativo: Tipo de perfil elegido al darse de alta.
        Reutiliza `profile_type` que ya devuelve /users/me. No permite editar.
      */}
      <Card
        className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
        data-testid="dashboard-profile-type"
      >
        <SectionTitle icon={IdCard} label="Tipo de perfil" hint="Se seleccionó al crear la cuenta." />
        {loading ? (
          <Skeleton className="h-6 w-56 rounded-lg" />
        ) : (
          <div className="flex flex-wrap items-center gap-2 text-[var(--ns-text)]">
            <span className="font-medium">Tipo de perfil:</span>
            <Badge
              variant="secondary"
              className="text-sm font-semibold bg-[var(--ns-gold)]/10 border border-[var(--ns-gold)]/25 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/15"
              data-testid="dashboard-profile-type-value"
            >
              {PROFILE_TYPE_LABELS[profileType] || (profileType || '—')}
            </Badge>
          </div>
        )}
      </Card>
      <ChangePasswordSection />
    </div>
  );
}
